import { useEffect, useRef, useState, type RefObject } from "react";
import { Box } from "~/common/mui";
import { ScrollSlider } from "~/features/search/components/ProductResults/views/NewTabularView/ScrollSlider";

/**
 * TableScrollController - An isolated scroll controller component.
 *
 * PURPOSE:
 * This component manages the ScrollSlider state independently from TanStackTable.
 * By isolating slider state here, we prevent the entire table from re-rendering
 * every time the slider position changes (which happens continuously during drag).
 *
 * HOW IT WORKS:
 * 1. This component owns `sliderValue` state internally.
 * 2. It subscribes to scroll events on the table container (via ref).
 * 3. When the table is scrolled (by mouse wheel, trackpad, or native scrollbar),
 *    the scroll event fires, and THIS component updates its internal state.
 * 4. When the user drags the slider, THIS component updates the table's scrollLeft
 *    directly via ref, without touching the parent's state.
 *
 * RESULT:
 * - Slider drag → Only this component re-renders (fast!)
 * - Table scroll → Only this component re-renders (slider thumb moves)
 * - Table data changes → Parent re-renders, but slider is unaffected
 *
 * REFS VS STATE:
 * We use refs (tableContainerRef, stickyHeaderRef) passed from the parent.
 * Refs are stable objects - passing them as props doesn't cause re-renders.
 * We read/write scrollLeft directly on the DOM via ref, bypassing React state.
 */

interface TableScrollControllerProps {
  /**
   * Ref to the main scrollable table container (the tbody wrapper).
   * We read scrollLeft from this on scroll, and write scrollLeft on slider drag.
   */
  tableContainerRef: RefObject<HTMLDivElement | null>;

  /**
   * Ref to the sticky header container (if using split sticky header variant).
   * We sync its scrollLeft with the main container.
   */
  stickyHeaderRef: RefObject<HTMLDivElement | null>;

  /**
   * Width of pinned-left columns in pixels.
   * Used to offset the slider so it starts after the pinned columns.
   * This value is stable (only changes when columns change, not on scroll).
   */
  pinnedLeftWidth: number;

  /**
   * Whether the table has a sticky header (affects positioning: sticky vs absolute).
   */
  stickyHeader?: boolean;

  /**
   * Optional classes for the component.
   */
  classes?: {
    root?: string;
    scrollSlider?: string;
  };
}

export function TableScrollController({
  classes = {},
  tableContainerRef,
  stickyHeaderRef,
  pinnedLeftWidth,
  stickyHeader,
}: TableScrollControllerProps) {
  // ============================================================================
  // STATE
  // ============================================================================

  /**
   * The slider's current value (0-100, representing scroll percentage).
   * This state is ISOLATED here - updating it does NOT re-render TanStackTable.
   */
  const [sliderValue, setSliderValue] = useState(0);

  /**
   * Flag to prevent scroll → slider sync while user is dragging the slider.
   * Without this, the scroll event would fight against the drag, causing jitter.
   */
  const isSliderDragging = useRef(false);

  // ============================================================================
  // SCROLL → SLIDER SYNC
  // ============================================================================

  /**
   * Subscribe to scroll events on both containers.
   * When the table scrolls (via mouse wheel, trackpad, or native scrollbar),
   * we update the slider position to match.
   */
  useEffect(() => {
    const tableContainer = tableContainerRef.current;
    const stickyHeaderEl = stickyHeaderRef.current;

    if (!tableContainer) {
      return;
    }

    /**
     * Handler for scroll events on the main table container.
     * - Calculates scroll percentage and updates slider position.
     * - Also syncs the sticky header's scroll position.
     */
    const handleScroll = () => {
      // Don't update slider while user is dragging it (prevents jitter)
      if (isSliderDragging.current) {
        return;
      }

      const maxScroll = tableContainer.scrollWidth - tableContainer.clientWidth;
      if (maxScroll > 0) {
        const scrollPercent = (tableContainer.scrollLeft / maxScroll) * 100;
        setSliderValue(scrollPercent);
      }

      // Keep sticky header in sync with main container
      if (stickyHeaderEl && stickyHeaderEl.scrollLeft !== tableContainer.scrollLeft) {
        stickyHeaderEl.scrollLeft = tableContainer.scrollLeft;
      }
    };

    /**
     * Handler for scroll events on the sticky header.
     * If user scrolls the header directly, sync the main container.
     */
    const handleStickyHeaderScroll = () => {
      if (stickyHeaderEl && tableContainer.scrollLeft !== stickyHeaderEl.scrollLeft) {
        tableContainer.scrollLeft = stickyHeaderEl.scrollLeft;
      }
    };

    // Use passive listeners for better scroll performance
    tableContainer.addEventListener("scroll", handleScroll, { passive: true });
    stickyHeaderEl?.addEventListener("scroll", handleStickyHeaderScroll, { passive: true });

    return () => {
      tableContainer.removeEventListener("scroll", handleScroll);
      stickyHeaderEl?.removeEventListener("scroll", handleStickyHeaderScroll);
    };
  }, [tableContainerRef, stickyHeaderRef]);

  // ============================================================================
  // SLIDER → SCROLL SYNC
  // ============================================================================

  /**
   * Called continuously while user drags the slider.
   * Updates the table's scrollLeft directly via ref (no parent re-render).
   */
  const handleSliderChange = (_: Event, newValue: number | number[]) => {
    const value = Array.isArray(newValue) ? newValue[0] : newValue;
    setSliderValue(value);

    const tableContainer = tableContainerRef.current;
    const stickyHeaderEl = stickyHeaderRef.current;

    if (tableContainer) {
      const maxScroll = tableContainer.scrollWidth - tableContainer.clientWidth;
      tableContainer.scrollLeft = (value / 100) * maxScroll;

      // FIX: Sync sticky header scroll when dragging slider
      // Without this, only the table body scrolls, leaving the header behind.
      if (stickyHeaderEl) {
        stickyHeaderEl.scrollLeft = tableContainer.scrollLeft;
      }
    }
  };

  /**
   * Called when user starts dragging the slider.
   * Sets flag to prevent scroll events from updating the slider position.
   */
  const handleSliderDragStart = () => {
    isSliderDragging.current = true;
  };

  /**
   * Called when user finishes dragging the slider (mouseup/touchend).
   * Clears the dragging flag so scroll events resume updating the slider.
   */
  const handleSliderChangeCommitted = () => {
    isSliderDragging.current = false;
  };

  // ============================================================================
  // RENDER
  // ============================================================================

  return (
    <Box
      sx={{
        position: stickyHeader ? "sticky" : "absolute",
        top: 0,
        left: 0,
        right: 0,
        zIndex: 1001,
        paddingLeft: `${pinnedLeftWidth}px`,
        paddingRight: (theme) => theme.spacing(2),
        height: (theme) => `calc(40px + ${theme.spacing(2)})`,
        display: "flex",
        alignItems: "center",
        pointerEvents: "none",
      }}
      className={classes.root}
    >
      <ScrollSlider
        value={sliderValue}
        onChange={handleSliderChange}
        onChangeCommitted={handleSliderChangeCommitted}
        onMouseDown={handleSliderDragStart}
        min={0}
        max={100}
        sx={{ pointerEvents: "auto" }}
        className={classes.scrollSlider}
      />
    </Box>
  );
}
